import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import type { ConfidenceRow } from '@/hooks/compare'

const CONFIDENCE_ORDER = [
  'established',
  'corroborated',
  'reported',
  'single_source',
  'unverified',
] as const

type ConfidenceKey = (typeof CONFIDENCE_ORDER)[number]

const COLOUR: Record<ConfidenceKey, string> = {
  established: 'var(--chart-success-strong)',
  corroborated: 'var(--chart-success)',
  reported: 'var(--chart-caution)',
  single_source: 'var(--chart-warning)',
  unverified: 'var(--chart-muted)',
}

const LABEL: Record<ConfidenceKey, string> = {
  established: 'Established',
  corroborated: 'Corroborated',
  reported: 'Reported',
  single_source: 'Single-source',
  unverified: 'Unverified',
}

function ConfidenceBar({
  ladder,
  baseline,
  total,
  showBaseline,
}: {
  ladder: ConfidenceRow[]
  baseline: ConfidenceRow[]
  total: number
  showBaseline: boolean
}) {
  if (total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No stories in this sector for the selected period.
      </p>
    )
  }

  const byLabel = Object.fromEntries(ladder.map((r) => [r.label, r.count]))
  const baseTotal = baseline.reduce((s, r) => s + r.count, 0)
  const byBaseline = Object.fromEntries(baseline.map((r) => [r.label, r.count]))

  return (
    <div className="space-y-3">
      {/* Sector bar */}
      <div>
        <p className="mb-1 text-xs text-muted-foreground">Sector</p>
        <div
          className="flex h-8 w-full overflow-hidden rounded-md"
          role="group"
          aria-label="Sector confidence distribution"
        >
          {CONFIDENCE_ORDER.map((label) => {
            const count = byLabel[label] ?? 0
            const pct = total > 0 ? (count / total) * 100 : 0
            if (pct === 0) return null
            return (
              <div
                key={label}
                style={{ width: `${pct}%`, backgroundColor: COLOUR[label] }}
                className="flex items-center justify-center text-xs text-white"
                title={`${LABEL[label]}: ${Math.round(pct)}%`}
              >
                {pct > 14 ? `${Math.round(pct)}%` : ''}
              </div>
            )
          })}
        </div>
      </div>
      {/* Corpus baseline bar */}
      {showBaseline && (
        <div>
          <p className="mb-1 text-xs text-muted-foreground">Corpus baseline</p>
          <div
            className="flex h-4 w-full overflow-hidden rounded-md opacity-50"
            role="group"
            aria-label="Corpus-wide confidence baseline"
          >
            {CONFIDENCE_ORDER.map((label) => {
              const count = byBaseline[label] ?? 0
              const pct = baseTotal > 0 ? (count / baseTotal) * 100 : 0
              if (pct === 0) return null
              return (
                <div
                  key={label}
                  style={{ width: `${pct}%`, backgroundColor: COLOUR[label] }}
                  title={`${LABEL[label]} (baseline): ${Math.round(pct)}%`}
                />
              )
            })}
          </div>
        </div>
      )}
      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {CONFIDENCE_ORDER.map((label) => (
          <span key={label} className="inline-flex items-center gap-1">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: COLOUR[label] }}
            />
            {LABEL[label]}
          </span>
        ))}
      </div>
    </div>
  )
}

export interface SectorConfidenceIndexProps {
  sectorLadder: ConfidenceRow[]
  corpusLadder: ConfidenceRow[]
  totalSector: number
  selectedSector: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

export function SectorConfidenceIndex({
  sectorLadder,
  corpusLadder,
  totalSector,
  selectedSector,
  isLoading,
  isError,
  onRetry,
}: SectorConfidenceIndexProps) {
  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Sector Confidence Index</h3>
        <Skeleton className="h-[120px] w-full" />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Sector Confidence Index</h3>
        <ErrorState title="Couldn't load confidence data." onRetry={onRetry} />
      </Card>
    )
  }

  return (
    <Card data-testid="compare-sector-confidence" className="p-4">
      <h3 className="mb-1 text-sm font-semibold">Sector Confidence Index</h3>
      <p className="mb-3 text-xs text-muted-foreground">
        {selectedSector === 'all'
          ? 'Corpus-wide confidence distribution.'
          : 'How well-corroborated is coverage of your sector vs the corpus baseline?'}
      </p>
      <ConfidenceBar
        ladder={sectorLadder}
        baseline={corpusLadder}
        total={totalSector}
        showBaseline={selectedSector !== 'all'}
      />
    </Card>
  )
}
